import { useEffect, useState, useRef } from "react";
import { OfflinePrefetchProps } from "../interface";
import { AppDispatch, RootState } from "app/services/store";
import { useSelector } from "react-redux";
import {
  getActiveRequestCount,
  waitForCapacity,
  delayBetweenRequests,
} from "./prefetchThrottleHelpers";
import {
  updatePrefetchMetadata,
  selectPrefetchMetadata,
} from "app/store/slices/prefetchMetadata";
import { TypedLazyQueryTrigger } from "@reduxjs/toolkit/dist/query/react";
import { DISABLE_OFFLINE_RETRIEVAL } from "app/config";

interface UsePrefetchUtilsConfig extends OfflinePrefetchProps {
  /** Optional prefetch handler function to execute automatically */
  prefetchHandler?: () => Promise<void>;
}

export function usePrefetchUtils({
  enabled,
  prefetchHandler,
}: UsePrefetchUtilsConfig) {
  const [hasPrefetchedAll, setHasPrefetchedAll] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Use ref to store the latest prefetchHandler without causing re-renders
  const prefetchHandlerRef = useRef(prefetchHandler);

  // Track previous enabled state to detect changes
  const prevEnabledRef = useRef(enabled);

  // Update ref when handler changes
  useEffect(() => {
    prefetchHandlerRef.current = prefetchHandler;
  }, [prefetchHandler]);

  const residentIds = useSelector(
    (state: RootState) => state?.residents?.residentIds ?? [],
  );
  const residentsPrefetched = useSelector(
    (state: RootState) => state?.residents?.residentsPrefetched,
  );

  useEffect(() => {
    // Only reset when enabled changes from false to true
    // This allows re-prefetching when the user re-enables offline mode
    if (enabled && !prevEnabledRef.current && hasPrefetchedAll) {
      setHasPrefetchedAll(false);
    }
    prevEnabledRef.current = enabled;
  }, [enabled, hasPrefetchedAll]);

  // Automatically execute prefetch when conditions are met
  useEffect(() => {
    if (
      enabled &&
      !isFetching &&
      !hasPrefetchedAll &&
      prefetchHandlerRef.current
    ) {
      setIsFetching(true);
      prefetchHandlerRef
        .current()
        .then(() => {
          setHasPrefetchedAll(true);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
    // Note: prefetchHandler is NOT in dependencies to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isFetching, hasPrefetchedAll]);

  return {
    hasPrefetchedAll,
    setHasPrefetchedAll,
    setIsFetching,
    isFetching,
    residentsPrefetched: residentsPrefetched && !!residentIds.length,
  };
}

/**
 * Configuration for paginated prefetch
 */
interface PaginatedPrefetchConfig<TQueryArgs, TResponse> {
  /** Name for logging (e.g., "FluidOutput", "FormEntries") */
  name: string;
  /** RTK Query lazy trigger function */
  trigger: TypedLazyQueryTrigger<TResponse, TQueryArgs, any>;
  /** Function to build query args for a specific page */
  buildQueryArgs: (page: number) => TQueryArgs;
  /** Function to store the response data in Redux */
  storeData: (data: TResponse) => void;
  /** Function to extract last page number from response */
  getLastPage: (data: TResponse) => number;
  /** Capacity threshold (default: 2) */
  capacityThreshold?: number;
  /** Optional: Enable smart caching (checks if first record changed) */
  enableSmartCache?: boolean;
  /** Optional: Function to extract first record ID from response */
  getFirstRecordId?: (data: TResponse) => number | string | null;
  /** Optional: Redux dispatch function (required if enableSmartCache is true) */
  dispatch?: AppDispatch;
  /** Optional: Redux state (required if enableSmartCache is true) */
  getState?: () => RootState;
  /** Optional: Max age in milliseconds before forcing refresh (default: 6 hours) */
  maxCacheAge?: number;
}

/**
 * Configuration for simple (non-paginated) prefetch
 */
interface SimplePrefetchConfig {
  /** Name for logging (e.g., "Locations", "Forms") */
  name: string;
  /** Function to execute the prefetch */
  fetchFn: () => Promise<void> | void;
  /** Capacity threshold (default: 2) */
  capacityThreshold?: number;
}

/**
 * Reusable paginated prefetch handler with throttling and smart caching
 * Handles capacity checking, pagination, delays, error handling, and cache validation
 */
export async function executePaginatedPrefetch<TQueryArgs, TResponse>({
  name,
  trigger,
  buildQueryArgs,
  storeData,
  getLastPage,
  capacityThreshold = 2,
  enableSmartCache = false,
  getFirstRecordId,
  dispatch,
  getState,
  maxCacheAge = 6 * 60 * 60 * 1000, // 6 hours default
}: PaginatedPrefetchConfig<TQueryArgs, TResponse>): Promise<void> {
  if (DISABLE_OFFLINE_RETRIEVAL) {
    console.log(`[Prefetch ${name}] Skipped (Offline retrieval disabled)`);
    return;
  }
  try {
    // Wait if there are too many active requests
    const initialActiveRequests = getActiveRequestCount();
    if (initialActiveRequests > capacityThreshold) {
      console.log(
        `[Prefetch ${name}] Waiting for capacity (${initialActiveRequests} active requests)...`,
      );
      await waitForCapacity(capacityThreshold);
    }

    // Check cache if smart caching is enabled
    let shouldFetchAllPages = true;
    if (enableSmartCache && getFirstRecordId && getState) {
      const metadata = selectPrefetchMetadata(getState(), name);
      const now = Date.now();
      const cacheAge = metadata ? now - metadata.lastFetchTimestamp : Infinity;

      // If cache is still fresh (within maxCacheAge)
      if (metadata && cacheAge < maxCacheAge) {
        console.log(
          `[Prefetch ${name}] Cache is ${Math.round(cacheAge / 1000 / 60)} minutes old, checking for changes...`,
        );
      }
    }

    // Fetch first page (prefer cache if available)
    console.log(`[Prefetch ${name}] Fetching first page...`);
    const { data } = await trigger(buildQueryArgs(1));

    if (!data) {
      console.warn(`[Prefetch ${name}] No data returned from first page`);
      return;
    }

    // Check if data has changed (smart cache)
    if (enableSmartCache && getFirstRecordId && getState && dispatch) {
      const firstRecordId = getFirstRecordId(data);
      const metadata = selectPrefetchMetadata(getState(), name);
      const now = Date.now();
      const cacheAge = metadata ? now - metadata.lastFetchTimestamp : Infinity;

      if (
        metadata &&
        metadata.firstRecordId === firstRecordId &&
        cacheAge < maxCacheAge
      ) {
        console.log(
          `[Prefetch ${name}] ✓ Data unchanged (first record ID: ${firstRecordId}), skipping remaining pages`,
        );
        shouldFetchAllPages = false;
      } else if (metadata && metadata.firstRecordId !== firstRecordId) {
        console.log(
          `[Prefetch ${name}] ⟳ Data changed (${metadata.firstRecordId} → ${firstRecordId}), fetching all pages`,
        );
      } else if (cacheAge >= maxCacheAge) {
        console.log(
          `[Prefetch ${name}] ⟳ Cache expired (${Math.round(cacheAge / 1000 / 60 / 60)}h old), fetching all pages`,
        );
      } else {
        console.log(`[Prefetch ${name}] ⟳ First fetch, fetching all pages`);
      }

      // Update metadata with new first record ID
      dispatch(updatePrefetchMetadata({ key: name, firstRecordId }));
    }

    storeData(data);

    // Handle pagination (only if data has changed or smart cache is disabled)
    const lastPage = getLastPage(data);
    if (shouldFetchAllPages && lastPage > 1) {
      for (let i = 2; i <= lastPage; i++) {
        // Check capacity before each paginated request
        const currentActiveRequests = getActiveRequestCount();
        if (currentActiveRequests > capacityThreshold) {
          console.log(
            `[Prefetch ${name}] Pausing at page ${i}/${lastPage} (${currentActiveRequests} active requests)...`,
          );
          await waitForCapacity(capacityThreshold);
        }

        // Add delay between requests to avoid bursts
        await delayBetweenRequests();

        const resp = await trigger(buildQueryArgs(i));
        if (resp.data) {
          storeData(resp.data);
        }
      }
    }

    console.log(
      `[Prefetch ${name}] Completed successfully (${lastPage} pages)`,
    );
  } catch (error) {
    console.error(`[Prefetch ${name}] Failed:`, error);
    throw error;
  }
}

/**
 * Reusable simple (non-paginated) prefetch handler with throttling
 * Handles capacity checking and error handling for single requests
 */
export async function executeSimplePrefetch({
  name,
  fetchFn,
  capacityThreshold = 2,
}: SimplePrefetchConfig): Promise<void> {
  if (DISABLE_OFFLINE_RETRIEVAL) {
    console.log(`[Prefetch ${name}] Skipped (Offline retrieval disabled)`);
    return;
  }
  try {
    // Wait if there are too many active requests
    const initialActiveRequests = getActiveRequestCount();
    if (initialActiveRequests > capacityThreshold) {
      console.log(
        `[Prefetch ${name}] Waiting for capacity (${initialActiveRequests} active requests)...`,
      );
      await waitForCapacity(capacityThreshold);
    }

    // Execute the fetch function
    await fetchFn();

    console.log(`[Prefetch ${name}] Completed successfully`);
  } catch (error) {
    console.error(`[Prefetch ${name}] Failed:`, error);
    throw error;
  }
}
